import { Dispatch, SetStateAction } from 'react'
import { Td, Tr } from '@chakra-ui/react'
import moment from 'moment'
import {
  AmountUnit,
  DurationUnit,
  FrequencyUnit,
  Method,
  Percentage,
  Regimen,
  SelectedFields,
  Usage,
  amountUnits,
  durationUnits,
  frequencyUnits,
  methods,
  percentages,
} from '../../types/Regimen'
import ActionButtons from './ActionButtons'

interface RegimensRowProps {
  record: Regimen
  usage: Usage
  usageIndex: number
  lists: {
    label: string
    fieldId: string
    items: { [itemId: string]: string }
    id: string
  }[]
  month: string
  year: string
  displayUsages: Usage[]
  amountEnabled?: boolean
  frequencyEnabled?: boolean
  durationEnabled?: boolean
  methodEnabled?: boolean
  percentageEnabled?: boolean
  allowUpdate: boolean // Non-optional boolean
  canReactivate?: boolean
  isGroupHeader?: boolean
  recordLabel: string
  reportingForStart: string
  setEditingId: Dispatch<SetStateAction<string | undefined>>
  setEditingUsageIndex: Dispatch<SetStateAction<number | undefined>>
  setStartDate: Dispatch<SetStateAction<string>>
  setFrequency: Dispatch<SetStateAction<string>>
  setFrequencyUnit: Dispatch<SetStateAction<FrequencyUnit | undefined>>
  setAmount: Dispatch<SetStateAction<string>>
  setAmountUnit: Dispatch<SetStateAction<AmountUnit | undefined>>
  setMethod: Dispatch<SetStateAction<Method | undefined>>
  setSelectedFields: Dispatch<SetStateAction<SelectedFields>>
  setDuration: Dispatch<SetStateAction<string>>
  setDurationUnit: Dispatch<SetStateAction<DurationUnit | undefined>>
  setPercentage: Dispatch<SetStateAction<Percentage | undefined>>
  modal: { onOpen: () => void }
  setConfirmEndId: Dispatch<SetStateAction<string | undefined>>
  setConfirmDelete: Dispatch<
    SetStateAction<{ recordId: string; usageIndex: number } | undefined>
  >
  onReactivate: (recordId: string) => void
}

export default function RegimensRow({
  record,
  usage,
  usageIndex,
  lists,
  month,
  year,
  displayUsages,
  amountEnabled,
  frequencyEnabled,
  durationEnabled,
  methodEnabled,
  percentageEnabled,
  allowUpdate,
  canReactivate = false,
  isGroupHeader = true,
  recordLabel,
  reportingForStart,
  setEditingId,
  setEditingUsageIndex,
  setStartDate,
  setFrequency,
  setFrequencyUnit,
  setAmount,
  setAmountUnit,
  setMethod,
  setSelectedFields,
  setDuration,
  setDurationUnit,
  setPercentage,
  modal,
  setConfirmEndId,
  setConfirmDelete,
  onReactivate,
}: RegimensRowProps) {
  // Row color - grey for archived/ended regimens
  const archivedColor = usageIndex !== 0 || record.endDate ? 'gray' : ''

  return (
    <Tr style={{ color: archivedColor }}>
      <ActionButtons
        usageIndex={usageIndex}
        record={record}
        usage={usage}
        allowUpdate={allowUpdate}
        canReactivate={canReactivate}
        reportingForStart={reportingForStart}
        setEditingId={setEditingId}
        setEditingUsageIndex={setEditingUsageIndex}
        setStartDate={setStartDate}
        setFrequency={setFrequency}
        setFrequencyUnit={setFrequencyUnit}
        setAmount={setAmount}
        setAmountUnit={setAmountUnit}
        setMethod={setMethod}
        setSelectedFields={setSelectedFields}
        setDuration={setDuration}
        setDurationUnit={setDurationUnit}
        setPercentage={setPercentage}
        modal={modal}
        setConfirmEndId={setConfirmEndId}
        setConfirmDelete={setConfirmDelete}
        onReactivate={onReactivate}
      />
      <Td>
        {usage.startDate
          ? moment(usage.startDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
          : `Before ${month}/${year}`}
      </Td>
      <Td>
        {usageIndex === 0
          ? record.endDate
            ? moment(record.endDate, 'YYYY-MM-DD').format('YYYY-MM-DD')
            : `Current`
          : moment(displayUsages[usageIndex - 1].startDate).format(
              'YYYY-MM-DD',
            )}
      </Td>
      {lists.map((list, index) => (
        <Td key={`${record.id}-${list.fieldId}`}>
          {isGroupHeader || index > 0
            ? list.items[usage.fields[list.fieldId]]
            : null}
        </Td>
      ))}
      {percentageEnabled && (
        <Td>{usage.percentage && percentages[usage.percentage]}</Td>
      )}
      {amountEnabled && (
        <Td>
          {usage.amount} {usage.amountUnit && amountUnits[usage.amountUnit]}
        </Td>
      )}
      {durationEnabled && (
        <Td>
          {usage.duration}{' '}
          {usage.durationUnit && durationUnits[usage.durationUnit]}
        </Td>
      )}
      {frequencyEnabled && (
        <Td>
          {usage.frequency}x/
          {usage.frequencyUnit && frequencyUnits[usage.frequencyUnit]}
        </Td>
      )}
      {methodEnabled && <Td>{usage.method ? methods[usage.method] : 'N/A'}</Td>}
    </Tr>
  )
}
